import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';

import { isWorking, PHASE, RUN_STATUS } from '../../constants/conversation';
import { useConversation } from '../../hooks/useConversation';
import { useMandateChips } from '../../hooks/useMandateChips';
import {
	useConfirmPlan,
	useCreateDraft,
	useDiscardDraft,
	useSaveRun,
	useSendMessage,
	useStopRun,
} from '../../models/mutations';
import { toMandatePayload } from '../../utils/mandatePayload';
import { planState } from '../../utils/planState';

import Composer from './Composer';
import EmptyState from './EmptyState';
import MessageList from './MessageList';
import ProfileRail from './ProfileRail';

const PLACEHOLDERS = {
	empty: 'Beschreiben Sie, welche Firmen Sie suchen …',
	[PHASE.RESULTS]: 'Eingrenzen, erweitern oder etwas prüfen lassen …',
	default: 'Antworten …',
};

/**
 * The assistant's chat inside the search popup. `runId` is the user's draft or a saved
 * conversation ("Mit Assistent fortsetzen"); without one the first message creates the draft.
 *
 * @param {object} props
 * @param {string|null} props.runId
 * @param {boolean} props.visible - The popup shows this chat (the stream lives only then)
 * @param {function} props.onOpenForm - (profile, mandate) => void: hand the profile to the search mask
 * @param {function} props.onSaved - ({ prospectQueryId, name }) => void
 * @param {function} props.onDraftCreated - (run) => void
 * @param {function} props.onDiscarded - () => void
 */
const ChatSearch = ({ runId, visible, onOpenForm, onSaved, onDraftCreated, onDiscarded }) => {
	const { run, conversation, preview, isLoading } = useConversation(runId, { visible });
	const createDraft = useCreateDraft();
	const sendMessage = useSendMessage();
	const confirmPlan = useConfirmPlan();
	const stopRun = useStopRun();
	const saveRun = useSaveRun();
	const discardDraft = useDiscardDraft();

	const [input, setInput] = useState('');
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	const [sentAt, setSentAt] = useState(null);

	// The chips are the mandate: answered at once, persisted before anything depends on them.
	const serverMandate = run?.mandate ?? conversation.mandate ?? null;
	const {
		form: mandate,
		change: changeMandate,
		flush: flushMandate,
		pending: mandatePending,
	} = useMandateChips(runId, serverMandate);

	const status = run?.status ?? conversation.status;
	const phase = run?.phase ?? conversation.phase ?? PHASE.COLLECT;
	const working = isWorking(status);
	const cancelled = status === RUN_STATUS.CANCELLED;
	// A message was sent and the stream has not answered yet: show the thinking dot.
	const thinking = working || (sentAt !== null && conversation.lastSeq <= sentAt);
	useEffect(() => {
		if (sentAt !== null && working) setSentAt(null);
	}, [working, sentAt]);
	const empty = conversation.turns.length === 0 && !isLoading;
	const hasProfile = Boolean(conversation.profile?.terms?.length || conversation.profile?.locations?.length);

	const send = async (text) => {
		// The chips are the mandate the message runs under: nothing is sent before they are saved.
		if (!(await flushMandate())) return;
		setInput('');
		let id = runId;
		try {
			if (!id) {
				const { run: created } = await createDraft.mutateAsync({ mandate: toMandatePayload(mandate) });
				id = created.id;
				onDraftCreated(created);
			}
			const answer = await sendMessage.mutateAsync({ runId: id, text });
			setSentAt(answer?.seq ?? Number.MAX_SAFE_INTEGER);
		} catch {
			// reported by the global MutationCache; the text is lost on purpose, the chat shows the error
		}
	};

	const blocks = {
		planState: (block) =>
			planState(block, {
				openProposalSeq: run?.openProposalSeq ?? conversation.openProposalSeq,
				mandatePending,
				working,
				cancelled,
			}),
		isLatestResult: (block) => conversation.results && block.seq === conversation.results.seq,
		onStart: async (block) => {
			if (!(await flushMandate())) return;
			confirmPlan.mutate({ runId, proposalSeq: block.proposalSeq });
		},
		onOpenForm: () => onOpenForm(conversation.profile, serverMandate),
		onSave: async (name) => {
			try {
				const result = await saveRun.mutateAsync({ runId, name });
				onSaved(result);
			} catch {
				// reported globally
			}
		},
		starting: confirmPlan.isPending,
		saving: saveRun.isPending,
	};

	let placeholder = PLACEHOLDERS.default;
	if (empty) placeholder = PLACEHOLDERS.empty;
	else if (phase === PHASE.RESULTS) placeholder = PLACEHOLDERS[PHASE.RESULTS];

	const composer = (
		<Composer
			value={input}
			onChange={setInput}
			onSend={send}
			onStop={() => stopRun.mutate(runId)}
			placeholder={placeholder}
			busy={cancelled || sendMessage.isPending || createDraft.isPending}
			working={working}
			chipsLocked={createDraft.isPending}
			mandate={mandate}
			onMandateChange={changeMandate}
		/>
	);

	return (
		<div className="flex min-h-0 flex-1">
			<div className="flex min-w-0 flex-1 flex-col bg-white">
				{empty ? (
					<EmptyState composer={composer} onPrompt={send} />
				) : (
					<>
						<MessageList
							conversation={conversation}
							preview={preview}
							thinking={thinking && !cancelled}
							blocks={blocks}
						/>
						<div className="bg-white px-5 pb-4 pt-2">
							{cancelled ? (
								<p className="text-center text-sm text-bgBase-800">Dieses Gespräch wurde verworfen.</p>
							) : (
								composer
							)}
						</div>
					</>
				)}
				{runId && !run?.savedAt && !cancelled && (
					<div className="flex justify-end border-t border-bgBase-400 px-5 py-2">
						<Button variant="outline" size="sm" onClick={() => setConfirmDiscard(true)}>
							Verwerfen
						</Button>
					</div>
				)}
			</div>
			{hasProfile && (
				<ProfileRail
					profile={conversation.profile}
					mandate={serverMandate}
					estimate={run?.estimate ?? conversation.estimate}
					onOpenForm={() => onOpenForm(conversation.profile, serverMandate)}
				/>
			)}
			<ConfirmModal
				isOpen={confirmDiscard}
				onClose={() => setConfirmDiscard(false)}
				onConfirm={async () => {
					setConfirmDiscard(false);
					try {
						await discardDraft.mutateAsync();
						onDiscarded();
					} catch {
						// reported globally
					}
				}}
				title="Gespräch verwerfen"
				message="Laufende Schritte werden gestoppt und Reservierungen freigegeben. Bereits gekaufte Firmen bleiben in ihrer Liste."
				confirmText="Verwerfen"
			/>
		</div>
	);
};

ChatSearch.propTypes = {
	runId: PropTypes.string,
	visible: PropTypes.bool.isRequired,
	onOpenForm: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	onDraftCreated: PropTypes.func.isRequired,
	onDiscarded: PropTypes.func.isRequired,
};

export default ChatSearch;
